import type { Location } from 'react-router';
import { useLocation } from 'react-router';

import { capitalize } from 'es-toolkit';

import { loansArchiveConfig } from '@skynet/loans-feature';
import { retainersArchiveConfig } from '@skynet/retainers-feature';
import { arbitrageConfig } from '@skynet/arbitrage-feature';
import { serversConfig } from '@skynet/servers-feature';
import { botCommandsConfig } from '@skynet/admin-bot-commands-feature';
import { botMessagesConfig } from '@skynet/bot-messages-feature';
import { tradingGroupsConfig } from '@skynet/trading-groups-feature';
import { smmConfig } from '@skynet/smm-feature';
import { dexExchangesConfig } from '@skynet/dex-exchanges-feature';
import { exchangesConfig } from '@skynet/exchanges-feature';
import { usersConfig } from '@skynet/users-feature';
import { regionsConfig } from '@skynet/regions-feature';
import { itemsConfig } from '@skynet/items-feature';
import { tenantsConfig } from '@skynet/tenants-feature';
import { rolesConfig } from '@skynet/roles-feature';
import { marketingConfig } from '@skynet/marketing-feature';
import { tokenMappingConfig } from '@skynet/token-mapping-feature';

import { ApplicationDomain } from 'src/routes/types';

import { useMemo, useState } from 'react';
import { useCheckNavigationAccess } from 'src/hooks/useCerbos/useCheckNavigationAccess';
import { getCerbosNavigationAction } from 'src/hooks/useCerbos/useAuthorizedNavigation';

// [ApplicationDomain.values without Profile]: Record<string, string>
export type SectionConfig = {
  [key in Exclude<ApplicationDomain, ApplicationDomain.Profile>]: Record<string, string>;
};

const domainsWithSections = [
  ApplicationDomain.Admin,
  ApplicationDomain.Loans,
  ApplicationDomain.Retainers,
  ApplicationDomain.Trading,
  ApplicationDomain.DEX,
];

const navKeys: SectionConfig = {
  [ApplicationDomain.Admin]: {
    [serversConfig.section]: serversConfig.section,
    [arbitrageConfig.section]: arbitrageConfig.section,
    log: 'log',
    'sub-accounts': 'sub-accounts',
    [usersConfig.section]: usersConfig.section,
    [rolesConfig.section]: rolesConfig.section,
    [tokenMappingConfig.section]: tokenMappingConfig.section,
    [exchangesConfig.section]: exchangesConfig.section,
    [regionsConfig.section]: regionsConfig.section,
    [botCommandsConfig.section]: botCommandsConfig.section,
    [botMessagesConfig.section]: botMessagesConfig.section,
    [tradingGroupsConfig.section]: tradingGroupsConfig.section,
    [marketingConfig.section]: marketingConfig.section,
    [tenantsConfig.section]: tenantsConfig.section,
    [itemsConfig.section]: itemsConfig.section,
    // Last, like its action in `uiNavigationActions`, so SMM is the default
    // admin section only for users who have no other one.
    [smmConfig.section]: smmConfig.section,
  },
  [ApplicationDomain.Loans]: {
    summary: 'summary',
    [loansArchiveConfig.section]: loansArchiveConfig.section,
    overview: 'overview',
    exchanges: 'overview',
    balances: 'balances',
    tasks: 'tasks',
    transactions: 'transactions',
    liquidity: 'liquidity',
    bots: 'bots',
    terms: 'terms',
  },
  [ApplicationDomain.Retainers]: {
    overview: 'overview',
    terms: 'terms',
    // exchanges: 'overview',
    balances: 'balances',
    bots: 'bots',
    'arbitrage-info': 'arbitrage-info',
    // bots: 'bots',
    tasks: 'tasks',
    transactions: 'transactions',
    'balances-admin': 'balances-admin',
    'overview-admin': 'overview-admin',
    'tasks-admin': 'tasks-admin',
    clients: 'clients',
    'terms-admin': 'terms-admin',
    [retainersArchiveConfig.section]: retainersArchiveConfig.section,
  },
  [ApplicationDomain.Trading]: {
    tasks: 'tasks',
    bots: 'bots',
    balances: 'balances',
    'dex-setup': 'dex-setup',
    reports: 'reports',
  },
  [ApplicationDomain.DEX]: {
    blockchains: 'blockchains',
    [dexExchangesConfig.section]: dexExchangesConfig.section,
    transactions: 'transactions',
    vaults: 'vaults',
    'vault-overview': 'vault-overview',
    'vault-tasks': 'vault-tasks',
    'vault-details': 'vault-details',
    'vault-transactions': 'vault-transactions',
    tasks: 'tasks',
  },
  // no sections for Profile
  // [ApplicationDomain.Profile]: {},
};

const noIdSections: Record<ApplicationDomain, string[]> = {
  [ApplicationDomain.Loans]: ['summary', loansArchiveConfig.section, 'not-found'],
  [ApplicationDomain.Retainers]: [retainersArchiveConfig.section, 'not-found'],
  [ApplicationDomain.Admin]: ['not-found'],
  [ApplicationDomain.Profile]: ['not-found'],
  [ApplicationDomain.Trading]: ['not-found', 'dex-setup'],
  [ApplicationDomain.DEX]: [
    'transactions',
    'vaults',
    'blockchains',
    dexExchangesConfig.section,
    'not-found',
    'tasks',
  ],
};

/**
 *
 * @param allowedPages Authorization data from Cerbos. Array of the keys (e.g. 'read:loans:summary')
 * @param presetDomain Should be passed if we want to get the default section for the specific domain
 * @returns [ApplicationDomain, string | undefined | null] First item - ApplicationDomain is set considering the data from authorization, second item - section could be null, if the domain has no sections (e.g. Profile)
 */
export function getDefaults(
  allowedPages: string[],
  presetDomain?: ApplicationDomain,
): [ApplicationDomain, string | null] {
  const domainsToCheck = [
    ApplicationDomain.Loans,
    ApplicationDomain.Retainers,
    ApplicationDomain.Admin,
    ApplicationDomain.Trading,
    ApplicationDomain.DEX,
  ] as const;

  // Type guard: ensures the value is one of the domains that have sections (a valid key for `navKeys`)
  const hasSections = (v: ApplicationDomain | undefined): v is keyof typeof navKeys =>
    v !== undefined && domainsWithSections.includes(v);

  // Checks domains and sections from the navKeys config and returns the first allowed domain/section pair.
  // If nothing is allowed, returns undefined for both domain and section.
  const allowedDefaults = domainsToCheck.reduce<
    [ApplicationDomain | undefined, string | undefined | null]
  >(
    (acc, domain) => {
      if (acc[0] && acc[1]) {
        return acc;
      }

      if (acc[0] === undefined) {
        acc[0] = domain;
      }

      if (!hasSections(acc[0])) {
        acc[1] = null;
        return acc;
      }
      // at this point acc[0] is defined and is a key of navKeys
      const domainKey = acc[0];
      const sections = Object.values(navKeys[domainKey]);
      const allowedSection = sections.find((section) => {
        const authzAction = getCerbosNavigationAction(domainKey, section);
        return allowedPages.includes(authzAction);
      });

      if (allowedSection) {
        acc[1] = allowedSection;
      } else {
        acc[1] = undefined; // No sections allowed for this domain
        acc[0] = undefined; // Current domain is not allowed, as auth data does not contain any allowed sections of this domain.
      }

      return acc;
    },
    [presetDomain, undefined],
  );

  const [first, second] = allowedDefaults;
  if (first === undefined) {
    // If no domain is allowed, return Profile as allowed domain
    return [ApplicationDomain.Profile, null];
  }

  return [first, second ?? null];
}

export function isNoIdSection(domain?: ApplicationDomain, section?: string | null): boolean {
  if (!section || !domain) {
    return true;
  }
  return Boolean(noIdSections[domain]?.includes(section));
}

/**
 * Default entity-capable section for a domain (url casing, e.g. 'loans'),
 * derived from the user's allowed pages. No-id sections (summary, archive,
 * ...) are excluded, so the result is safe for `/{domain}/{section}/{id}`
 * URLs. Empty string when the domain has no allowed sections.
 */
export function getDefaultSectionForCurrentDomain(data: string[], domain?: string) {
  if (data.length === 0 || !domain) {
    return '';
  }
  const filterDomainWithSection = data.filter((item) => {
    const [, uiDomain, section] = item.split(':');
    if (uiDomain !== domain.toLowerCase()) {
      return false;
    }
    return !((noIdSections as Record<string, string[]>)[capitalize(domain)] ?? []).includes(
      section,
    );
  });
  const [, defaultSection] = getDefaults(filterDomainWithSection ?? []);
  return defaultSection ?? '';
}

export interface GetSectionParams {
  domain: keyof typeof navKeys;
  location: Location;
}

/** The section segment of a path, lowercased: `/loans/archive/2` → `archive`, `''` if none. */
const getSectionSegment = (pathname: string): string =>
  pathname.split('/').filter(Boolean)[1]?.toLowerCase() ?? '';

export function getSection({ domain, location }: GetSectionParams): string {
  const sections = navKeys[domain];
  const sectionKey = getSectionSegment(location.pathname);

  switch (domain) {
    case ApplicationDomain.Admin:
    case ApplicationDomain.Loans:
    case ApplicationDomain.Retainers:
    case ApplicationDomain.DEX:
    case ApplicationDomain.Trading:
      return sections[sectionKey] ?? '';
  }
  return '';
}

/**
 * Key for `SkyLayout`'s `contentTransitionKey`: the raw section segment of the URL
 * (`/loans/archive/2` → `archive`, `''` on the domain root).
 *
 * Unlike `useSection().section`, it is a string from the first render and tells
 * apart routed sections missing from `navKeys` (e.g. `arbitrage-info`, `not-found`),
 * so every section change remounts and cross-fades the content.
 */
export function useSectionTransitionKey(): string {
  const { pathname } = useLocation();
  return getSectionSegment(pathname);
}

interface SectionProps {
  domain?: ApplicationDomain;
}

interface SectionData {
  isLoading: boolean;
  domain?: ApplicationDomain;
  section?: string | null;
  isNoIdSection?: boolean;
  defaultSection: string;
}

export const isApplicationDomain = (value: unknown): value is ApplicationDomain =>
  typeof value === 'string' &&
  (Object.values(ApplicationDomain) as unknown[]).some((v) => v === value);

export const isDomainWithSections = (value: unknown): value is keyof typeof navKeys =>
  typeof value === 'string' && domainsWithSections.map(String).includes(value);

export function useSection({ domain: presetDomain }: SectionProps = {}): SectionData {
  const [domain, setDomain] = useState<ApplicationDomain | undefined>();
  const [section, setSection] = useState<string | undefined | null>();
  const location = useLocation();
  const { data, isLoading } = useCheckNavigationAccess();
  const [urlDomain] = location.pathname.split('/').filter(Boolean);

  const defaultSection = useMemo(
    () => getDefaultSectionForCurrentDomain(data ?? [], urlDomain),
    [data, urlDomain],
  );

  if (urlDomain === 'not-found') {
    return {
      domain: undefined,
      section: null,
      isLoading,
      isNoIdSection: true,
      defaultSection: '',
    };
  }

  const domainKeyRaw = capitalize(presetDomain || urlDomain || '');

  const domainKey = isApplicationDomain(domainKeyRaw) ? domainKeyRaw : undefined;

  if (!domainKey || !Object.values(ApplicationDomain).includes(domainKey)) {
    const [defaultDomain, defaultSection] = getDefaults(data ?? []);

    if (defaultDomain !== domain) {
      setDomain(defaultDomain);
    }
    if (defaultSection !== section) {
      setSection(defaultSection);
    }
  } else if (!domainsWithSections.includes(domainKey)) {
    if (domainKey !== domain) {
      setDomain(domainKey);
      setSection(null);
    }
  } else {
    const sectionByLocation = isDomainWithSections(domainKey)
      ? getSection({ domain: domainKey, location })
      : undefined;
    if (sectionByLocation !== section) {
      setSection(sectionByLocation);
    }
    if (domainKey !== domain) {
      setDomain(domainKey);
    }
  }

  return {
    section,
    domain,
    isLoading,
    isNoIdSection: isNoIdSection(domainKey, section),
    defaultSection,
  };
}
